import type { NodeViewProps } from '@tiptap/react';
import { NodeViewWrapper } from '@tiptap/react';
import { selectVariableForChip } from '../../../../grid/reduxStore/allVariableSlice';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDisplayVariable, getVariableFlagState } from './utils';
import { getAllEncounteredValidationCodes, getRenderedValidationCodes } from 'services/entities/variableValidation';
import { trackVariableValidationError } from 'utils/trackVariableValidationError';
import useFeatureFlag from '../../../../../../hooks/useFeatureFlag.ts';
import { FeatureFlags } from '../../../../../../utils/featureFlags.ts';
import { useAppSelector, type RootState } from 'components/editor/grid/reduxStore/Store';
import {
  DEFAULT_CURRENCY_CODE,
  selectActiveCurrencySymbol,
  selectContentType,
} from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { DOLLAR_SIGN } from '../../../Block/Table/defaultData.tsx';
import Tooltip from '@mui/material/Tooltip';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';

const VALIDATION_ERROR_TRANSLATION_PREFIX = 'editor.variables.validation_error';

export const VariableNodeView = ({ node, getPos, editor }: NodeViewProps) => {
  const { t } = useTranslation();
  const isCurrencySettingsEnabled = useFeatureFlag([FeatureFlags.currencySettings])[FeatureFlags.currencySettings];

  const activeCurrencySymbol = useSelector((state: RootState) => selectActiveCurrencySymbol(state)) || DEFAULT_CURRENCY_CODE;
  const currencySymbol = isCurrencySettingsEnabled ? activeCurrencySymbol : DOLLAR_SIGN;

  const contentType = useAppSelector(selectContentType);
  const isConflict = !!node.attrs.isConflict;
  const resolvedVariableNode = useSelector((state: RootState) =>
    selectVariableForChip(state, { name: node.attrs.name, objectId: node.attrs.objectId, template: node.attrs.template })
  );
  const variableNode = isConflict ? undefined : resolvedVariableNode;
  const displayInfo = variableNode ? getDisplayVariable(variableNode, contentType, currencySymbol) : undefined;

  useEffect(() => {
    if (!variableNode) return;
    getAllEncounteredValidationCodes(variableNode).forEach((code) => trackVariableValidationError(code, variableNode.name));
  }, [variableNode]);

  const onClick = () => {
    editor.chain().focus().setNodeSelection(getPos()).run();
  };

  const fallbackName = node.attrs.friendlyName || node.attrs.name || '';
  const hasInlineValue = node.attrs.value !== null && node.attrs.value !== undefined;
  // Checked independent of hasInlineValue — reflects the live node, not the chip's inline snapshot.
  const renderedValidationCodes = variableNode ? getRenderedValidationCodes(variableNode) : [];
  const isFailedValidation = renderedValidationCodes.length > 0;
  const notAssigned =
    !isFailedValidation && !hasInlineValue && getVariableFlagState(variableNode ?? undefined, contentType) === 'not-assigned';
  const className = `tiptap-variable-node-view${notAssigned && !isConflict ? ' no-value' : ''}${
    isFailedValidation ? ' has-validation-error' : ''
  }${isConflict ? ' has-array-conflict' : ''}`;
  const content = hasInlineValue ? String(node.attrs.value) : displayInfo ? displayInfo.displayValue : fallbackName;

  const validationTooltipTitle = isFailedValidation
    ? renderedValidationCodes
        .map((code) =>
          t(`${VALIDATION_ERROR_TRANSLATION_PREFIX}.${code}`, { defaultValue: t(`${VALIDATION_ERROR_TRANSLATION_PREFIX}.generic`) })
        )
        .join(' ')
    : undefined;
  const tooltipTitle = isConflict
    ? t('editor.variables.crm_empty_state.array_conflict_chip_tooltip', { path: node.attrs.path?.replace(/^All\/Integrations\//, '') })
    : validationTooltipTitle ?? (hasInlineValue ? node.attrs.friendlyName ?? '' : displayInfo?.tooltipTitle ?? '');

  const inner = (
    <NodeViewWrapper as="span" className={className} style={{ whiteSpace: 'pre-wrap' }} onClick={onClick}>
      {content}
      {isFailedValidation && (
        <ErrorOutlineRounded color="error" sx={{ fontSize: '0.875rem', verticalAlign: 'text-bottom', marginLeft: '2px' }} />
      )}
    </NodeViewWrapper>
  );

  return tooltipTitle ? (
    <Tooltip
      title={tooltipTitle}
      enterDelay={400}
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', overflowWrap: 'normal', wordBreak: 'normal' } } }}
    >
      {inner}
    </Tooltip>
  ) : (
    inner
  );
};
